import { useTranslation } from 'react-i18next';
import { AutoLinkPattern } from '../types';
import { Box, IconButton, Skeleton, Typography } from '@mui/material';
import LoadingPlaceholder from '@shared/components/ui/LoadingPlaceHolder';
import UtmsButton from '@shared/components/ui/UtmsButton';
import { FC } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

interface DefectAutoLinkPatternsListProps {
    patterns: AutoLinkPattern[];
    isDefectLoaded: boolean;
    isDefectLoading: boolean;
    onAdd: () => void;
    onEdit: (index: number) => void;
    onDelete: (index: number) => void;
}

const DefectAutoLinkPatternsList: FC<DefectAutoLinkPatternsListProps> = ({
    patterns,
    isDefectLoaded,
    isDefectLoading,
    onAdd,
    onEdit,
    onDelete,
}) => {
    const { t } = useTranslation();
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <LoadingPlaceholder
                    isLoading={isDefectLoading}
                    skeleton={
                        <>
                            <Skeleton animation="wave" width={104} />
                            <Skeleton variant="rounded" animation="wave" width={110} height={36} />
                        </>
                    }
                >
                    {isDefectLoaded && (
                        <>
                            <Typography sx={{ fontWeight: 700 }}>{t('automation_rules')}</Typography>
                            <UtmsButton startIcon={<AddIcon />} onClick={onAdd}>
                                {t('add')}
                            </UtmsButton>
                        </>
                    )}
                </LoadingPlaceholder>
            </Box>
            {patterns.length ? (
                patterns.map((pattern, index) => (
                    <Box key={pattern.pk} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                flexDirection: 'column',
                                maxWidth: '100%',
                                overflowWrap: 'break-word',
                            }}
                        >
                            <Typography>
                                #{index + 1} {pattern.name}
                            </Typography>
                            <Typography>{pattern.errorMessagePattern}</Typography>
                            <Typography>{pattern.stackTracePattern}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                            <IconButton onClick={() => onEdit(index)}>
                                <EditIcon />
                            </IconButton>
                            <IconButton color="error" onClick={() => onDelete(index)}>
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    </Box>
                ))
            ) : (
                <>
                    {isDefectLoaded && (
                        <Typography sx={{ color: 'text.secondary' }}>
                            {t('no_objects_yet', { objects: t('automation_rules_r') })}
                        </Typography>
                    )}
                    {isDefectLoading && <Skeleton animation="wave" width={140} />}
                </>
            )}
        </Box>
    );
};

export default DefectAutoLinkPatternsList;
